import { useEffect, useRef, useState } from 'react'
import { View } from 'react-native'
import RecordRTC from 'recordrtc'
import { useTranslation } from 'react-i18next'

import { AuthContext } from '@/stores/auth'
import { useToast } from '@/components/base'

import {
  CameraPermissionMessage,
  MAX_RECORDING_DURATION_SECONDS,
  PlayButton,
  RecordingControls,
  RecordingInstructions,
  type RecordingSectionProps,
  type RecordStatus
} from './RecordingSection.shared'

export function KYCRecordingSection({
  videoUri,
  addVideo,
  deleteVideo,
  diagnostics,
  onSubmit,
  resetKey
}: RecordingSectionProps) {
  const { t } = useTranslation()
  const [status, setStatus] = useState<RecordStatus>('preparing')
  const [time, setTime] = useState(0)
  const [recordRTC, setRecordRTC] = useState<RecordRTC | null>(null)
  const [permission, setPermission] = useState(true)
  const streamRef = useRef<MediaStream | null>(null)
  const cameraRef = useRef<HTMLVideoElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const toast = useToast()
  const userId = AuthContext.useSelector((state) => state.context.values?.user.id)

  const isVideoShown = ['waiting', 'playing', 'paused', 'played'].includes(status)
  const isCameraShown = ['preparing', 'recording', 'processing'].includes(status)

  useEffect(() => {
    let disposed = false
    diagnostics.event('web-recording-screen-mounted')

    navigator.mediaDevices
      .getUserMedia({ video: true, audio: true })
      .then((stream) => {
        if (disposed) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }

        const recorder = new RecordRTC(stream, {
          type: 'video',
          recorderType: RecordRTC.MediaStreamRecorder
        })
        setRecordRTC(recorder)
        streamRef.current = stream
        diagnostics.event('web-camera-stream-ready', {
          videoTrackCount: stream.getVideoTracks().length,
          audioTrackCount: stream.getAudioTracks().length
        })

        if (cameraRef.current) {
          cameraRef.current.srcObject = stream
        }
      })
      .catch((error: unknown) => {
        diagnostics.error('web-camera-stream-failed', error)
        console.error('Unable to access the camera and microphone', error)
        setPermission(false)
      })

    return () => {
      disposed = true
      diagnostics.event('web-recording-screen-unmounted')
      streamRef.current?.getTracks().forEach((track) => track.stop())
    }
  }, [diagnostics])

  useEffect(() => {
    if (cameraRef.current && streamRef.current) {
      cameraRef.current.srcObject = streamRef.current
    }
  }, [isCameraShown])

  useEffect(() => {
    if (status !== 'recording') {
      return
    }

    const id = window.setTimeout(() => {
      const nextTime = time + 1
      setTime(nextTime)
      if (nextTime >= MAX_RECORDING_DURATION_SECONDS) {
        stopRecording()
      }
    }, 1000)

    return () => window.clearTimeout(id)
  }, [status, time])

  function handleRecordButton() {
    diagnostics.event('web-record-button-pressed', {
      recordingStatus: status,
      recorderReady: Boolean(recordRTC)
    })
    if (status === 'preparing' && recordRTC) {
      setTime(0)
      setStatus('recording')
      recordRTC.startRecording()
      diagnostics.event('web-recording-started')
      return
    }

    if (status === 'recording') {
      stopRecording()
      return
    }

    toast.show({ variant: 'error', description: t('kyc:unableToRecordVideo') })
  }

  function stopRecording() {
    if (!recordRTC) {
      diagnostics.warn('web-recording-stop-blocked', { reason: 'recorder-not-ready' })
      toast.show({ variant: 'error', description: t('kyc:unableToRecordVideo') })
      return
    }

    diagnostics.event('web-recording-stop-requested', { recordingElapsedSeconds: time })
    setStatus('processing')
    recordRTC.stopRecording(() => {
      const uri = recordRTC.toURL()
      const blob = recordRTC.getBlob()
      diagnostics.event('web-recording-completed', {
        fileSizeBytes: blob.size,
        mimeType: blob.type || 'unknown'
      })
      addVideo(uri, blob)
      setStatus('waiting')
    })
  }

  function handleToggle() {
    const video = videoRef.current
    if (!video) {
      return
    }

    if (status === 'playing') {
      video.pause()
      return
    }

    if (status === 'played') {
      video.currentTime = 0
    }
    video.play().catch((error: unknown) => {
      diagnostics.error('web-recorded-video-play-failed', error)
      console.error('Unable to play the identity video', error)
      toast.show({ variant: 'error', description: t('kyc:unableToPlayVideo') })
    })
  }

  function handleReset() {
    diagnostics.event('web-recording-reset-requested')
    videoRef.current?.pause()
    setStatus('preparing')
    setTime(0)
    deleteVideo()
    resetKey()
  }

  return (
    <>
      <RecordingInstructions userId={userId} />
      <View className={'flex-1'}>
        {isCameraShown &&
          (permission ? (
            <View className={'absolute inset-0'}>
              <video autoPlay playsInline ref={cameraRef} muted className={'size-full object-cover'} />
            </View>
          ) : (
            <CameraPermissionMessage />
          ))}
        {isVideoShown && (
          <View className={'absolute inset-0 [&_video]:size-full'}>
            <video
              ref={videoRef}
              src={videoUri}
              playsInline
              className={'size-full object-cover'}
              onPlay={() => setStatus('playing')}
              onPause={(event) => {
                if (event.currentTarget.currentTime < event.currentTarget.duration) {
                  setStatus('paused')
                }
              }}
              onEnded={(event) => {
                setTime(Math.min(MAX_RECORDING_DURATION_SECONDS, Math.ceil(event.currentTarget.duration)))
                setStatus('played')
              }}
              onTimeUpdate={(event) => setTime(Math.floor(event.currentTarget.currentTime))}
            />
            <PlayButton onPress={handleToggle} isPlaying={status === 'playing'} />
          </View>
        )}
      </View>
      <RecordingControls
        status={status}
        time={time}
        cameraReady={Boolean(recordRTC)}
        onRecord={handleRecordButton}
        onReset={handleReset}
        onSubmit={() => {
          diagnostics.event('kyc-submit-button-pressed', {
            recordingStatus: status,
            recordedVideoPresent: Boolean(videoUri)
          })
          onSubmit()
        }}
      />
    </>
  )
}
